import { useEffect, useMemo, useReducer } from 'react';

import { gql, useApolloClient } from '@apollo/client';

type DisplayNameItem = {
  resourceName: string;
  displayName?: string;
  typename?: string;
};

export const readDisplayNameFromCache = (
  client: ReturnType<typeof useApolloClient>,
  resourceName: string,
  typename?: string,
): string | undefined => {
  if (!typename) return undefined;
  const cached = client.cache.readFragment<{ displayName?: string }>({
    id: client.cache.identify({ __typename: typename, name: resourceName }),
    fragment: gql`
      fragment DisplayNameLookup_${typename} on ${typename} {
        displayName
      }
    `,
    fragmentName: `DisplayNameLookup_${typename}`,
  });
  return cached?.displayName ?? undefined;
};

const readDisplayNames = (
  client: ReturnType<typeof useApolloClient>,
  items: DisplayNameItem[],
): Map<string, string> => {
  const map = new Map<string, string>();
  for (const item of items) {
    const cached = readDisplayNameFromCache(
      client,
      item.resourceName,
      item.typename,
    );
    if (cached) {
      map.set(item.resourceName, cached);
      continue;
    }

    if (item.displayName) {
      map.set(item.resourceName, item.displayName);
    }
  }
  return map;
};

export const useResolvedDisplayNames = (
  items: DisplayNameItem[],
): Map<string, string> => {
  const client = useApolloClient();
  const [cacheVersion, rerender] = useReducer((x: number) => x + 1, 0);

  useEffect(() => {
    if (items.length === 0) return undefined;

    const subscriptions = items
      .map(item => {
        const typename = item.typename;
        if (!typename) return undefined;

        const fragment = gql`
          fragment DisplayNameWatch_${typename} on ${typename} {
            displayName
          }
        `;

        return client.cache
          .watchFragment({
            fragment,
            fragmentName: `DisplayNameWatch_${typename}`,
            from: { __typename: typename, name: item.resourceName },
          })
          .subscribe({ next: rerender });
      })
      .filter(Boolean) as { unsubscribe: () => void }[];

    return () => subscriptions.forEach(s => s.unsubscribe());
  }, [items, client, rerender]);

  return useMemo(() => {
    void cacheVersion;
    return readDisplayNames(client, items);
  }, [items, client, cacheVersion]);
};
